// Half-day driver holidays vs a booking's timeframe.
//
// Port of the legacy rule (search_results.php `timeframeConflictsWithHoliday`):
//   - FULL holiday              → conflicts with every timeframe.
//   - AM holiday                → conflicts with AM and AM-PM bookings.
//   - PM holiday                → conflicts with PM and AM-PM bookings.
// An AM-PM booking spans the whole day, so either half-day holiday removes
// half of it — it conflicts (the warning is non-blocking, staff can proceed).
// Two records on the same date that disagree (e.g. one AM + one PM) together
// block the whole day, i.e. the effective period is FULL.

export type HolidayPeriod = 'FULL' | 'AM' | 'PM';

export interface PeriodHoliday {
  start_date: string;
  end_date: string;
  period: HolidayPeriod;
}

export function normalisePeriod(value: string | null | undefined): HolidayPeriod {
  return value === 'AM' || value === 'PM' ? value : 'FULL';
}

export function periodLabel(period: HolidayPeriod): string {
  if (period === 'AM') return 'AM only';
  if (period === 'PM') return 'PM only';
  return 'Full day';
}

/** Collapse several records' periods on one date into the effective period. */
export function effectivePeriod(periods: ReadonlyArray<HolidayPeriod>): HolidayPeriod | null {
  if (periods.length === 0) return null;
  const first = periods[0] as HolidayPeriod;
  return periods.every((p) => p === first) ? first : 'FULL';
}

/** Does a booking in `timeframe` clash with a holiday of the given period? */
export function timeframeConflicts(
  timeframe: string,
  period: HolidayPeriod | null,
): boolean {
  if (period === null) return false;
  if (period === 'FULL') return true;
  const tf = timeframe.trim().toUpperCase();
  return tf === period || tf === 'AM-PM' || tf === 'AM/PM';
}

export function holidaysCoveringDate<T extends PeriodHoliday>(
  holidays: ReadonlyArray<T>,
  date: string,
): T[] {
  return holidays.filter((h) => date >= h.start_date && date <= h.end_date);
}

/** Effective holiday period for a date across all records (null = no holiday). */
export function effectivePeriodOnDate(
  holidays: ReadonlyArray<PeriodHoliday>,
  date: string,
): HolidayPeriod | null {
  return effectivePeriod(holidaysCoveringDate(holidays, date).map((h) => h.period));
}

/**
 * Holiday records that clash with a booking on `date` in `timeframe`. Empty
 * when the driver is free for that slot (e.g. AM booking vs a PM-only holiday).
 */
export function conflictingHolidays<T extends PeriodHoliday>(
  holidays: ReadonlyArray<T>,
  date: string,
  timeframe: string,
): T[] {
  const covering = holidaysCoveringDate(holidays, date);
  const effective = effectivePeriod(covering.map((h) => h.period));
  return timeframeConflicts(timeframe, effective) ? covering : [];
}
